import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, AlertCircle, RefreshCw, FileDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Personnalisation() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [refreshing, setRefreshing] = useState(false);
  const tableRef = useRef(null);

  const fetchData = async (d) => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/stats/departments?date_filter=${d}`);
      setData(res.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(date); }, [date]);

  const totalRow = data.reduce((acc, d) => ({
    total: acc.total + d.total,
    on_time: acc.on_time + d.on_time,
    late: acc.late + d.late,
    absent_scanned: acc.absent_scanned + d.absent_scanned,
    non_scanned: acc.non_scanned + d.non_scanned,
  }), { total: 0, on_time: 0, late: 0, absent_scanned: 0, non_scanned: 0 });

  const tauxGlobal = totalRow.total > 0
    ? Math.round((totalRow.on_time + totalRow.late) / totalRow.total * 100) : 0;

  const exportPDF = () => {
    const dateFormatted = new Date(date + "T12:00:00").toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric"
    });

    const rows = data.map((dept, i) => {
      const taux = dept.total > 0 ? Math.round((dept.on_time + dept.late) / dept.total * 100) : 0;
      const tauxColor = taux >= 80 ? "#16a34a" : taux >= 50 ? "#b45309" : "#dc2626";
      const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
      return `
        <tr style="background:${bg}">
          <td style="padding:10px 14px;font-weight:600;color:#1e293b;border-bottom:1px solid #e2e8f0">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#f97316;margin-right:8px"></span>
            ${dept.department_name}
          </td>
          <td style="padding:10px 14px;text-align:center;border-bottom:1px solid #e2e8f0">
            <span style="background:#fff7ed;color:#c2410c;padding:3px 10px;border-radius:20px;font-weight:700;font-size:13px">${dept.total}</span>
          </td>
          <td style="padding:10px 14px;text-align:center;border-bottom:1px solid #e2e8f0">
            <span style="background:#dcfce7;color:#16a34a;padding:3px 10px;border-radius:20px;font-weight:700;font-size:13px">${dept.on_time}</span>
          </td>
          <td style="padding:10px 14px;text-align:center;border-bottom:1px solid #e2e8f0">
            <span style="background:#fef9c3;color:#b45309;padding:3px 10px;border-radius:20px;font-weight:700;font-size:13px">${dept.late}</span>
          </td>
          <td style="padding:10px 14px;text-align:center;border-bottom:1px solid #e2e8f0">
            <span style="background:#fee2e2;color:#dc2626;padding:3px 10px;border-radius:20px;font-weight:700;font-size:13px">${dept.absent_scanned}</span>
          </td>
          <td style="padding:10px 14px;text-align:center;border-bottom:1px solid #e2e8f0">
            <span style="background:#f1f5f9;color:#475569;padding:3px 10px;border-radius:20px;font-weight:700;font-size:13px">${dept.non_scanned}</span>
          </td>
          <td style="padding:10px 14px;text-align:center;border-bottom:1px solid #e2e8f0">
            <div style="display:flex;align-items:center;justify-content:center;gap:6px">
              <div style="width:50px;height:6px;background:#e2e8f0;border-radius:3px;overflow:hidden">
                <div style="width:${taux}%;height:100%;background:${tauxColor};border-radius:3px"></div>
              </div>
              <span style="font-weight:700;color:${tauxColor};font-size:12px">${taux}%</span>
            </div>
          </td>
        </tr>`;
    }).join("");

    const tauxGlobalColor = tauxGlobal >= 80 ? "#16a34a" : tauxGlobal >= 50 ? "#b45309" : "#dc2626";

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Départements - ${dateFormatted}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: Arial, sans-serif; padding: 30px; color: #1e293b; }
    @media print { body { padding: 15px; } table { page-break-inside: auto; } tr { page-break-inside: avoid; } }
  </style>
</head>
<body>
  <div style="background:linear-gradient(135deg,#f97316,#ea580c);border-radius:16px;padding:28px 32px;margin-bottom:28px;color:white">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px">
      <div>
        <h1 style="font-size:26px;font-weight:800;margin-bottom:4px">Statistiques par Département</h1>
        <p style="opacity:0.85;font-size:14px">${dateFormatted}</p>
      </div>
      <div style="background:rgba(255,255,255,0.2);border-radius:12px;padding:12px 20px;text-align:center">
        <div style="font-size:32px;font-weight:800;color:${tauxGlobal >= 80 ? '#4ade80' : tauxGlobal >= 50 ? '#fbbf24' : '#f87171'}">${tauxGlobal}%</div>
        <div style="font-size:11px;opacity:0.85">Taux global</div>
      </div>
    </div>
    <div style="display:flex;gap:12px;margin-top:20px;flex-wrap:wrap">
      ${[
        { label: "Total", value: totalRow.total, color: "white" },
        { label: "À temps", value: totalRow.on_time, color: "#4ade80" },
        { label: "Retard", value: totalRow.late, color: "#fbbf24" },
        { label: "Abs. scannés", value: totalRow.absent_scanned, color: "#f87171" },
        { label: "Non scannés", value: totalRow.non_scanned, color: "#cbd5e1" },
      ].map(s => `
        <div style="background:rgba(255,255,255,0.15);border-radius:10px;padding:10px 16px;text-align:center;min-width:90px">
          <div style="font-size:22px;font-weight:800;color:${s.color}">${s.value}</div>
          <div style="font-size:10px;opacity:0.85">${s.label}</div>
        </div>`).join("")}
    </div>
  </div>
  <div style="background:white;border-radius:16px;box-shadow:0 2px 20px rgba(0,0,0,0.08);overflow:hidden">
    <table style="width:100%;border-collapse:collapse">
      <thead>
        <tr style="background:#f8fafc">
          ${["Département","Total","À temps","Retard","Abs. scannés","Non scannés","Taux"].map(h =>
            `<th style="padding:14px;text-align:${h==='Département'?'left':'center'};font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.05em;border-bottom:1px solid #e2e8f0">${h}</th>`
          ).join("")}
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr style="background:#fff7ed;border-top:2px solid #fed7aa">
          <td style="padding:14px;font-weight:800;color:#c2410c;font-size:13px">TOTAL GÉNÉRAL</td>
          <td style="padding:14px;text-align:center;font-weight:800;color:#c2410c">${totalRow.total}</td>
          <td style="padding:14px;text-align:center;font-weight:800;color:#16a34a">${totalRow.on_time}</td>
          <td style="padding:14px;text-align:center;font-weight:800;color:#b45309">${totalRow.late}</td>
          <td style="padding:14px;text-align:center;font-weight:800;color:#dc2626">${totalRow.absent_scanned}</td>
          <td style="padding:14px;text-align:center;font-weight:800;color:#475569">${totalRow.non_scanned}</td>
          <td style="padding:14px;text-align:center;font-weight:800;color:${tauxGlobalColor}">${tauxGlobal}%</td>
        </tr>
      </tfoot>
    </table>
  </div>
  <div style="margin-top:24px;text-align:center;color:#94a3b8;font-size:11px">
    Généré par ScannerBenj Pro — ${new Date().toLocaleString("fr-FR")}
  </div>
  <script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) toast.error("Autorisez les popups pour exporter le PDF");
  };

  const tauxColor = (t) => t >= 80 ? "text-emerald-600" : t >= 50 ? "text-amber-600" : "text-rose-600";
  const tauxBg = (t) => t >= 80 ? "bg-emerald-500" : t >= 50 ? "bg-amber-500" : "bg-rose-500";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-5">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2.5 rounded-xl">
              <Building2 className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-manrope font-bold text-slate-900 tracking-tight">Départements</h1>
              <p className="text-sm text-slate-500">Statistiques par département</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="date" value={date}
              onChange={e => setDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white"
            />
            <button onClick={() => fetchData(date)} disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
              <RefreshCw className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")} />
              Actualiser
            </button>
            <button onClick={exportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm">
              <FileDown className="h-4 w-4" />Exporter PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Résumé global */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-5 mb-6 text-white shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-orange-100 text-sm font-medium mb-1">
                {new Date(date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </p>
              <p className="text-white font-bold text-lg">{data.length} département{data.length > 1 ? "s" : ""}</p>
            </div>
            <div className="bg-white/20 rounded-xl px-5 py-3 text-center">
              <p className={"text-3xl font-extrabold " + (tauxGlobal >= 80 ? "text-green-300" : tauxGlobal >= 50 ? "text-yellow-300" : "text-red-300")}>{tauxGlobal}%</p>
              <p className="text-xs text-orange-100 mt-0.5">Taux global</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: "Total ouvriers", value: totalRow.total, color: "text-white" },
              { label: "À temps", value: totalRow.on_time, color: "text-green-300" },
              { label: "Retard", value: totalRow.late, color: "text-yellow-300" },
              { label: "Abs. scannés", value: totalRow.absent_scanned, color: "text-red-300" },
              { label: "Non scannés", value: totalRow.non_scanned, color: "text-slate-300" },
            ].map(s => (
              <div key={s.label} className="bg-white/15 rounded-xl py-3 px-3 text-center">
                <p className={"text-2xl font-extrabold " + s.color}>{s.value}</p>
                <p className="text-xs text-orange-100 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tableau */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-3"></div>
            <p className="text-slate-400 text-sm">Chargement...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center shadow-sm">
            <AlertCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucun département trouvé</p>
          </div>
        ) : (
          <div ref={tableRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {["Département","Total","À temps","Retard","Abs. scannés","Non scannés","Taux"].map(h => (
                      <th key={h} className={"py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide " + (h === "Département" ? "text-left" : "text-center")}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((dept, i) => {
                    const taux = dept.total > 0 ? Math.round((dept.on_time + dept.late) / dept.total * 100) : 0;
                    return (
                      <tr key={dept.department_id} className={"border-b border-slate-100 transition-colors " + (i % 2 === 0 ? "bg-white" : "bg-slate-50/40") + " hover:bg-orange-50/30"}>
                        <td className="py-3.5 px-4 font-semibold text-slate-900">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                            {dept.department_name}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full text-xs font-bold">{dept.total}</span>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-bold">{dept.on_time}</span>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full text-xs font-bold">{dept.late}</span>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="bg-rose-100 text-rose-700 px-2.5 py-1 rounded-full text-xs font-bold">{dept.absent_scanned}</span>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-bold">{dept.non_scanned}</span>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-14 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className={"h-full rounded-full " + tauxBg(taux)} style={{ width: `${taux}%` }} />
                            </div>
                            <span className={"text-xs font-bold " + tauxColor(taux)}>{taux}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-orange-50 border-t-2 border-orange-200">
                    <td className="py-3.5 px-4 font-extrabold text-orange-800 text-sm">TOTAL GÉNÉRAL</td>
                    <td className="py-3.5 px-4 text-center font-extrabold text-orange-700">{totalRow.total}</td>
                    <td className="py-3.5 px-4 text-center font-extrabold text-emerald-700">{totalRow.on_time}</td>
                    <td className="py-3.5 px-4 text-center font-extrabold text-amber-700">{totalRow.late}</td>
                    <td className="py-3.5 px-4 text-center font-extrabold text-rose-700">{totalRow.absent_scanned}</td>
                    <td className="py-3.5 px-4 text-center font-extrabold text-slate-600">{totalRow.non_scanned}</td>
                    <td className="py-3.5 px-4 text-center font-extrabold text-orange-700">{tauxGlobal}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
